/*
 * Copyright (c) 2026. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useMemo, useState } from "react";
import _isEmpty from "lodash/isEmpty";
import * as messagesActions from "@/src/store/messages/actions";
import * as loginActions from "@/src/store/login/actions";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import PersonalDataForm from "./PersonalDataForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

export default function AccountPersonalData() {
  const { customer, refreshCustomer, isFetching: customerIsFetching } = useCustomer();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [newEmailAddress, setNewEmailAddress] = useState<string>("");
  const [externalErrors, setExternalErrors] = useState({});
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });

  const rerenderKey = useMemo(()=> new Date().toISOString(), [customer])

  if (!customer || isLoading || customerIsFetching) {
    return (
      <ContentBox>
        <LegalbirdLoader />
      </ContentBox>
    );
  }

  const initialValues = {
    gender: customer.person.gender,
    familyName: customer.person.familyName,
    givenName: customer.person.givenName,
    telephone: customer.telephone,
    email: customer.email,
  };

  const handleSubmit = async ({ values }: { values: FormDataTypes["values"] }) => {
    setIsLoading(true);
    if (values.email && values.email !== customer.email) {
      const emailCheck = await loginActions.checkEmail(values.email);
      if (emailCheck.hasError) {
        setIsLoading(false);
        setExternalErrors({
          email: emailCheck.errorText || "Es existiert bereits ein Konto mit dieser E-Mail-Adresse",
        });
        return;
      }
      setNewEmailAddress(values.email);
      setIsEmailModalOpen(true);
    }

    try {
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: {
          gender: values.gender || null,
          givenName: values.givenName || customer.person.givenName,
          familyName: values.familyName || customer.person.familyName,
        },
      });
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: {
          telephone: values.telephone,
        },
      });
      refreshCustomer();
    } catch (err: any) {
      if (!_isEmpty(err.errors)) {
        setExternalErrors(err.errors);
      } else {
        messagesActions.addMessage({
          type: "error",
          text:
            "Es ist ein technischer Fehler aufgetreten. Bitte laden Sie die Seite einmal neu. Wenn der Fehler bestehen bleibt, melden Sie sich bei unserem Kundenservice. Nennen Sie uns dann die Stelle, an der der Fehler aufgetreten ist und die Fehlermeldung: " +
            err.message,
        });
      }
    }
    setIsLoading(false);
  };

  return (
    <ContentBox headline={"Persönliche Daten"} showDivider={true}>
      <FormProvider key={rerenderKey} initialValues={initialValues} submitAction={handleSubmit} externalErrors={externalErrors}>
        <PersonalDataForm
          isLoading={isLoading}
          isEmailModalOpen={isEmailModalOpen}
          setIsEmailModalOpen={setIsEmailModalOpen}
          newEmailAddress={newEmailAddress}
        />
      </FormProvider>
    </ContentBox>
  );
}
